import { Tooltip } from "@mui/material";
import { capitalizeText, isTruthy } from "helpers/methods";
import { useAppSelector } from "hooks/use-app-select";
import { FileField } from "models/interfaces";
import React, { useEffect, useState } from "react";
import {
  setSelectedField,
  updateFieldInSignRequest,
  updateFieldOfSelectedFile,
} from "redux/appSlice";
import { useAppDispatch } from "redux/hooks";
import { fieldColorFor } from "screens/SignRequest/SignRequestHelper";
import { signerFieldValidationPatterns } from "global/constants/ConstantObject";
import strings from "global/constants/StringConstants";

/** A sender-typed pattern that does not compile is treated as "no check". */
const safeRegExp = (pattern: string): RegExp | null => {
  try {
    return new RegExp(pattern);
  } catch {
    return null;
  }
};

const ReadOnlyFields = (props: {
  field: FileField;
  coords?: any;
  inputPadding?: number;
}) => {
  const { selectedField, signRequest } = useAppSelector((state) => state.app);
  const dispatch = useAppDispatch();
  // In "I'm the only signer" the sender IS the signer, so this box is where the value is entered,
  // not where a default is configured for someone else.
  const isSelfSign = signRequest?.selfSigned === true;

  const getInputPadding = () => {
    const width = window.innerWidth;
    if (width <= 424) return 0;
    if (width <= 899) return 2;
    if (width <= 1439) return 0;
    return 4;
  };

  const allowedCharsByPattern: Record<string, RegExp> = {
    Numbers: /^[\d\s\r\n]*$/,
    Letters: /^[a-zA-Z\s\r\n]*$/,
    SSN: /^[\d-]*$/,
    Zip: /^\d*$/,
    "Zip+4": /^[\d-]*$/,
  };

  const isDropDown = props.field.type === strings.dropDown;
  const isLibraryField = !!(props.field as any).customFieldId;
  /**
   * A library text field is pre-filled through `defaultValue` -- the same property the Properties
   * panel and the Custom Field modal edit -- so typing on the document changes that one value
   * instead of creating a second, competing one in `value`. A field dragged from the Fields panel
   * keeps writing `value`, exactly as it always did.
   */
  const editsDefaultValue =
    !isSelfSign && isLibraryField && props.field.type === "text";
  // A dropdown is picked by the signer, so in self-sign it is a real select here; otherwise the
  // editor only previews the default the sender chose in the Properties panel.
  const picksDropDownValue = isSelfSign && isDropDown;
  const shownValue = editsDefaultValue
    ? (props.field.defaultValue ?? "")
    : isDropDown && !isSelfSign
      ? (props.field.defaultValue ?? "")
      : (props.field.value ?? "");

  const isAllowedKeystroke = (val: string): boolean => {
    if (
      props.field.type !== "text" ||
      props.field.customValidation ||
      !props.field.validationPattern ||
      props.field.validationPattern === "none"
    ) {
      return true;
    }
    const allowed = allowedCharsByPattern[props.field.validationPattern];
    return allowed ? allowed.test(val) : true;
  };

  const validateValue = (val: string): string => {
    if (
      props.field.type === "text" &&
      props.field.validationPattern &&
      props.field.validationPattern !== "none" &&
      val !== ""
    ) {
      const pattern = props.field.customValidation
        ? safeRegExp(props.field.validationPattern)
        : signerFieldValidationPatterns[
            props.field
              .validationPattern as keyof typeof signerFieldValidationPatterns
          ];
      if (pattern && !pattern.test(val)) {
        return props.field.validationMessage || "Value is not valid.";
      }
    }
    return "";
  };
  // Only a library field is flagged here: its default is a sender-set value, so breaking its own
  // rule is worth showing before the send is refused. A Fields-panel field shows nothing, exactly
  // as it did before -- the editor never marked those.
  const error = editsDefaultValue ? validateValue(shownValue) : "";

  const handleOnChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const val = e.target.value;
    if (!isAllowedKeystroke(val)) {
      return;
    }
    // Outside self-sign a library text field holds its text in `defaultValue` (what the signer
    // will start from); in self-sign the sender is the signer, so it is the `value` itself.
    const key = editsDefaultValue ? "defaultValue" : "value";
    // Default Value and the value on the document are one value -- see the note in
    // PropertiesDrawer.updateSelectedField. Typing here has to move both, or a correction to an
    // already-sent envelope would keep the value the signer was originally given.
    const patch: any = { [key]: val };
    if (editsDefaultValue) {
      patch.value = val;
    }
    const change = { id: props.field.id, ...patch };
    dispatch(updateFieldOfSelectedFile(change));
    dispatch(updateFieldInSignRequest(change));
    // The Properties panel renders its box from state.app.selectedField, which the two updates
    // above do not touch -- the panel keeps it in step itself when you type there. Without this
    // the sync only worked panel -> document. Library fields only, so a Fields-panel field
    // dispatches exactly what it always did. The reducer merges, so send only the key that
    // changed rather than a whole snapshot that could write back stale copies of the rest.
    if (isLibraryField && selectedField?.id === props.field.id) {
      dispatch(setSelectedField(patch));
    }
  };

  const getType = (type: string) => {
    if (type.includes("_")) {
      type = type.replace("_", " ");
    }
    return capitalizeText(type);
  };

  const getHeight = () => {
    // if (props.coords.pageH < props.field.styles.size) {
    //   console.log("check step",props.coords.pageH,  props.field.styles.size )
    //   return props.field.styles.size;
    // }
    return props.coords.pageH || props.field.styles?.size || 16;
    // return props.coords.pageH;
  };

  const color = fieldColorFor(props.field.signerIndex);
  const isEmail = props.field.type === "email";
  // Dropdowns are picked by the signer, so the editor only shows the default -- unless the sender
  // IS the signer, where the select below is rendered instead of this textarea.
  const isStatic = isEmail || (isDropDown && !isSelfSign);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!isDragging) return;
    const handleUp = () => setIsDragging(false);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchend", handleUp);
    return () => {
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchend", handleUp);
    };
  }, [isDragging]);

  // Self-sign only: the sender is the signer, so a dropdown has to be selectable here. Writes
  // `value`, the same property every other field writes in this flow.
  if (picksDropDownValue) {
    const options = (props.field.listItems ?? []).filter((item) =>
      isTruthy(item?.text),
    );
    return (
      <select
        aria-label={props.field.label || "Drop down"}
        value={props.field.value ?? ""}
        onChange={(e) => {
          const change = { id: props.field.id, value: e.target.value };
          dispatch(updateFieldOfSelectedFile(change));
          dispatch(updateFieldInSignRequest(change));
          if (selectedField?.id === props.field.id) {
            dispatch(setSelectedField({ value: e.target.value }));
          }
        }}
        style={{
          outline: "none",
          height: getHeight(),
          width: props.coords.pageW,
          fontSize: props.field.styles?.size,
          fontFamily: props.field.styles?.fontFamily,
          color: props.field.styles?.color,
          border: "none",
          boxSizing: "border-box",
          backgroundColor: props.field.required ? `${color}2A` : "transparent",
          padding: getInputPadding() ?? props.inputPadding,
          position: "absolute",
          top: 0,
          left: 0,
        }}
      >
        <option value="">Select…</option>
        {options.map((item) => (
          <option key={item.id ?? item.text} value={item.text}>
            {item.text}
          </option>
        ))}
      </select>
    );
  }

  const textarea = (
    <textarea
      title={error || undefined}
      aria-invalid={error ? true : undefined}
      style={{
        // A value that breaks the field's own rule is marked here rather than silently
        // waiting for the send to be refused.
        outline: error ? "1.5px solid #d32f2f" : "none",
        height: getHeight(),
        width: props.coords.pageW,
        fontSize: props.field.styles?.size,
        fontFamily: props.field.styles?.fontFamily,
        color: props.field.styles?.color,
        fontWeight: props.field.styles?.fontWeight?.bold ? "bold" : "unset",
        fontStyle: props.field.styles?.fontWeight?.italic ? "italic" : "unset",
        textDecoration: props.field.styles?.fontWeight?.underline
          ? "underline"
          : "none",
        border: "none",
        resize: "none",
        boxSizing: "border-box",
        backgroundColor: props.field.required ? `${color}2A` : `transparent`,
        padding: getInputPadding() ?? props.inputPadding,
        position: "absolute",
        top: "0",
        left: "0",
        ...(isStatic && { pointerEvents: "none" }),
        overflow: "hidden",
        whiteSpace: props.field.multiLine ? "pre-wrap" : "pre",
      }}
      wrap={props.field.multiLine ? "soft" : "off"}
      placeholder={
        (props.field.type === "text" || isDropDown) &&
        props.field.placeholder
          ? props.field.placeholder
          : isDropDown
            ? "Drop down \u25BE"
            : getType(props.field.type)
      }
      onChange={handleOnChange}
      autoFocus={props.field.readOnly && props.field.id === selectedField.id}
      readOnly={isStatic}
      maxLength={
        props.field.maxLength && props.field.maxLength > 0
          ? props.field.maxLength
          : undefined
      }
      value={shownValue}
      id={`readOnly-input-field-${props.field.id}`}
      onTouchEnd={(e) => {
        (e.target as HTMLTextAreaElement).focus();
      }}
    />
  );

  if (isEmail && props.field.value) {
    return (
      <Tooltip
        title={isDragging ? "" : props.field.value}
        arrow
        placement="top"
      >
        <span
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: props.coords.pageW,
            height: getHeight(),
          }}
          onMouseDown={() => setIsDragging(true)}
          onTouchStart={() => setIsDragging(true)}
        >
          {textarea}
        </span>
      </Tooltip>
    );
  }

  return textarea;
};

export default ReadOnlyFields;
